import React from 'react';
import { Container, Body5 } from '@ui';
import styled from 'styled-components';
import { NodeParameterRemoveButton } from '@flow/buttons/NodeParameterRemoveButton';
import { TransparentFlatButton } from '@ui/buttons';
import { AgentFlowParameterWrapper, AgentFlowParameterLabelWrapper } from '@flow/styles';
import { copyObject } from '@utils/usefulFuncs';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { PlanStepIdInput } from './PlanStepIdInput';
import { SettingsInput } from './SettingsInput';

const AddKeyButtonWrapper = styled.div`
  button {
    padding: 6.5px 12px;
  }
`;

const functionNames = [
  '',
  'resolve_docs_from_analysis_response',
  'split_inputs_into_queries',
  'split_step_ids_outputs_into_queries',
  'substitute_step_settings_in_queries',
  'substitute_step_outputs_in_queries',
  'apply_settings_from_step_settings',
  'apply_settings_from_step_outputs',
];

const blankKeyedItem = { ignore: '' };

// function keyedObjectValue(item) {
//   return Object.values(item)[0].join(',');
// }

function keyedObjectStringValue(item, keyIndex) {
  return Object.values(item)[keyIndex];
}

const StringListInput = ({ keyName, value, onChange }) => {
  React.useEffect(() => {
    if (!Array.isArray(value)) {
      onChange(['']);
    }
  }, [value]);

  if (!Array.isArray(value)) {
    return null;
  }
  const onInputChange = (e, index) => {
    const inputValue = e.target.value;
    const copyValue = [...value];
    copyValue[index] = inputValue;
    onChange(copyValue);
  };
  const onAddInput = () => {
    const copyValue = [...value];
    copyValue.push('');
    onChange(copyValue);
  };
  const onRemoveItem = (index) => {
    const copyValue = [...value];
    copyValue.splice(index, 1);
    onChange(copyValue);
  };
  return (
    <Container fullWidth>
      <Container fullWidth>
        {value.map((settingName, settingIndex) => (
          <Container key={settingIndex} fullWidth bottom={8} row verticallyCenter>
            {keyName === 'step_ids' ? (
              <PlanStepIdInput value={settingName} onChange={(e) => onInputChange(e, settingIndex)} />
            ) : keyName === 'settings_of_step_ids' ? (
              <SettingsInput value={settingName} onChange={(e) => onInputChange(e, settingIndex)} />
            ) : (
              <input value={settingName} onChange={(e) => onInputChange(e, settingIndex)} />
            )}
            <Container left={0}>
              <NodeParameterRemoveButton onClick={() => onRemoveItem(settingIndex)} />
            </Container>
          </Container>
        ))}
      </Container>

      <Container fullWidth alignRight row top={0} right={12} className="read-only-hide">
        <TransparentFlatButton onClick={onAddInput}>Add Item</TransparentFlatButton>
      </Container>
    </Container>
  );
};

const generatePostProcessOptionKeys = (item, currentKeyValue) => {
  const usedKeys = Object.keys(item).filter((key) => key !== currentKeyValue);
  const options = ['function', 'step_ids', 'query_constructor', 'context_query_constructor', 'settings_of_step_ids'];
  return options
    .filter((option) => !usedKeys.includes(option))
    .map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ));
};

const SettingsOfStepIds = ({ value, onChange }) => {
  const items = value;
  const onStepIdChange = (e, index) => {
    const newItems = copyObject(items);
    const newValue = e.target.value;
    newItems[index].step_id = newValue;
    onChange(newItems);
  };
  const onSettingNamesChange = (e, index) => {
    const newItems = copyObject(items);
    const newValue = e.target ? e.target.value : e;
    newItems[index].setting_names = newValue;
    onChange(newItems);
  };

  return (
    <Container fullWidth>
      {items.map((item, index) => (
        <Container key={index} fullWidth bottom={8}>
          <Container row>
            <Container row verticallyCenter style={{ width: '160px' }}>
              <Body5>Step ID:</Body5>
            </Container>
            <PlanStepIdInput value={item.step_id} onChange={(e) => onStepIdChange(e, index)} />
          </Container>
          <Container row top={8}>
            <Container row style={{ width: '160px' }}>
              <Container top={6}>
                <Body5>Setting Names:</Body5>
              </Container>
            </Container>
            <StringListInput
              keyName={'settings_of_step_ids'}
              value={item.setting_names}
              onChange={(v) => onSettingNamesChange(v, index)}
            />
          </Container>
        </Container>
      ))}
    </Container>
  );
};

export const FunctionListParameter = ({ data, value, onRemove, onChange }) => {
  const { keyName, label } = data;

  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    const syncItems = () => {
      if (!!value) {
        setItems(value);
      }
    };
    syncItems();
  }, []);

  React.useEffect(() => {
    onChange(
      keyName,
      items.filter((el) => el !== '')
    );
  }, [items]);

  const handleAddKeyedItem = () => {
    setItems((prevItems) => [...prevItems, blankKeyedItem]);
  };

  const handleAddKeyToKeyedItem = (i) => {
    setItems((prevItems) => {
      const copy = [...prevItems];
      copy[i] = { ...copy[i], ...blankKeyedItem };
      return copy;
    });
  };

  const handleRemoveClick = () => {
    onRemove(keyName);
  };

  const handleRemoveItemClick = (i, keyIndex) => {
    setItems((prevItems) => {
      const copy = [...prevItems];
      copy[i] = Object.fromEntries(Object.entries(copy[i]).filter(([k, _], idx) => idx !== keyIndex));
      return copy;
    });
  };

  const handleExtendedListKeyChange = (e, i, item, keyIndex) => {
    const newKey = e.target.value;
    const currentKey = getKeyByKeyIndex(item, keyIndex);
    const currentValue = keyedObjectStringValue(item, keyIndex);
    setItems((prevItems) => {
      const copy = [...prevItems];
      const entry = { ...copy[i] };
      // Delete the old key and set the new key with the old value
      delete entry[currentKey];
      entry[newKey] = currentValue;
      copy[i] = entry;
      return copy;
    });
  };

  const handleExtendedListValueChange = (e, i, item, keyIndex) => {
    const textValue = e.target.value;
    const value = { [getKeyByKeyIndex(item, keyIndex)]: textValue };
    setItems((prevItems) => {
      const copy = [...prevItems];
      copy[i] = { ...copy[i], ...value };
      return copy;
    });
  };

  const getKeyByKeyIndex = (item, keyIndex) => {
    return Object.keys(item)[keyIndex];
  };
  const getKeyValueByKeyIndex = (item, keyIndex) => {
    return Object.values(item)[keyIndex];
  };

  return (
    <AgentFlowParameterWrapper>
      <Container fullWidth>
        <Container row verticallyCenter spaceBetween fullWidth>
          <Container row verticallyCenter>
            <AgentFlowParameterLabelWrapper>
              <Body5>{label}:</Body5>
            </AgentFlowParameterLabelWrapper>
            <div className="read-only-hide">
              <TransparentFlatButton onClick={handleAddKeyedItem}>Add List Item</TransparentFlatButton>
            </div>
          </Container>
          <NodeParameterRemoveButton onClick={handleRemoveClick} />
        </Container>
        <ul>
          {items.map((item, index) => {
            return (
              <li key={index}>
                {Object.entries(item).map(([key, value], keyIndex) => (
                  <Container key={key + keyIndex} bottom={16}>
                    <Container fullWidth row verticallyCenter top={8} bottom={16}>
                      <Container row fullWidth>
                        <select
                          value={getKeyByKeyIndex(item, keyIndex)}
                          onChange={(e) => handleExtendedListKeyChange(e, index, item, keyIndex)}
                        >
                          <option value={'ignore'}>Please select...</option>
                          {generatePostProcessOptionKeys(item, getKeyByKeyIndex(item, keyIndex))}
                        </select>
                      </Container>
                      <NodeParameterRemoveButton onClick={() => handleRemoveItemClick(index, keyIndex)} />
                    </Container>
                    <Container row left={16} style={{ width: '371px' }}>
                      {getKeyByKeyIndex(item, keyIndex) === 'step_ids' ? (
                        <StringListInput
                          keyName={'step_ids'}
                          value={getKeyValueByKeyIndex(item, keyIndex)}
                          onChange={(v) =>
                            handleExtendedListValueChange({ target: { value: v } }, index, item, keyIndex)
                          }
                        />
                      ) : getKeyByKeyIndex(item, keyIndex) === 'settings_of_step_ids' ? (
                        <SettingsOfStepIds
                          value={getKeyValueByKeyIndex(item, keyIndex)}
                          onChange={(v) =>
                            handleExtendedListValueChange({ target: { value: v } }, index, item, keyIndex)
                          }
                        />
                      ) : getKeyByKeyIndex(item, keyIndex) === 'function' ? (
                        <select
                          value={keyedObjectStringValue(item, keyIndex)}
                          onChange={(e) => handleExtendedListValueChange(e, index, item, keyIndex)}
                        >
                          {functionNames.map((functionName) => (
                            <option key={functionName} value={functionName}>
                              {functionName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <textarea
                          value={getKeyValueByKeyIndex(item, keyIndex)}
                          onChange={(e) => handleExtendedListValueChange(e, index, item, keyIndex)}
                        />
                      )}
                    </Container>
                  </Container>
                ))}
                <Container fullWidth row bottom={8} className="read-only-hide">
                  <AddKeyButtonWrapper>
                    <SkeletonButton onClick={() => handleAddKeyToKeyedItem(index)}>Add Key</SkeletonButton>
                  </AddKeyButtonWrapper>
                </Container>
              </li>
            );
          })}
        </ul>
      </Container>
    </AgentFlowParameterWrapper>
  );
};
